// libraries
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

// queries
import { useGetFreightLanesQuery } from 'services/freightLanes'

// helpers and utils
import { TRANSPORTATION_TYPE_OPTIONS, ROUNDING_PRECISIONS } from 'utils/constants'
import { convertUM } from 'utils/helpers'
import { roundTo } from 'utils/data-manipulation/valueFormatter'

export const useCalculateFreightCost = ({
  contract,
  location,
  transportationType,
  carrierId,
  effectiveDate,
  netWeight,
  ticketType,
}) => {
  const { defaultYardUm } = useSelector(state => state.settings)

  const { data: freightLanesData, isFetching: isLoadingFreightLanesData } = useGetFreightLanesQuery(
    {
      filter: JSON.stringify({
        carrierId,
        effectiveDate,
      }),
    },
    {
      skip: !carrierId,
    },
  )

  /**
   * Returns the base subtotal freight lane active price based on the carrier id, transportation type, location, and freight lanes data
   * @returns {Number}
   */
  const freightLaneActivePriceData = useMemo(() => {
    if (!freightLanesData || !transportationType) return null
    return (
      (freightLanesData?.Items || []).find(
        freightLane =>
          freightLane.carrier.id === carrierId &&
          freightLane.transportationMode ===
            TRANSPORTATION_TYPE_OPTIONS.find(option => option.id === transportationType)?.name &&
          freightLane.location?.city?.toUpperCase() === location?.city?.toUpperCase() &&
          freightLane.location?.state?.toUpperCase() === location?.state?.toUpperCase(),
      )?.activePrice || null
    )
  }, [freightLanesData, location?.city, location?.state, carrierId, transportationType])

  /**
   * Returns the location freight cost based on the ticket type
   * @returns {Number}
   */
  const locationFreightCost = useMemo(() => {
    if (!location?.freightPricing) return null
    const { consumerFreightEnabled, consumerFreightCost, supplierFreightEnabled, supplierFreightCost } =
      location.freightPricing
    if (ticketType === 'OBT') {
      return consumerFreightEnabled ? consumerFreightCost : null
    } else if (ticketType === 'IBT') {
      return supplierFreightEnabled ? supplierFreightCost : null
    }
    return null
  }, [location, ticketType])

  const { contractFreightCost, contractFreightChargeback } = useMemo(() => {
    if (!contract?.freightPricing) return { contractFreightCost: null, contractFreightChargeback: null }
    const { consumerFreightEnabled, consumerFreightCost, consumerFreightChargeback } = contract.freightPricing
    return {
      contractFreightCost: consumerFreightEnabled ? consumerFreightCost : null,
      contractFreightChargeback: consumerFreightEnabled ? consumerFreightChargeback : null,
    }
  }, [contract])

  /**
   * Returns the location freight chargeback based on the ticket type
   * @returns {Number}
   */
  const locationFreightChargeback = useMemo(() => {
    if (!location?.freightPricing) return null
    const { consumerFreightEnabled, supplierFreightEnabled, consumerFreightChargeback, supplierFreightChargeback } =
      location.freightPricing
    if (ticketType === 'OBT') {
      if (consumerFreightEnabled) {
        return consumerFreightChargeback
      }
      return null
    } else if (ticketType === 'IBT') {
      if (supplierFreightEnabled) {
        return supplierFreightChargeback
      }
      return null
    }
    return null
  }, [location, ticketType])

  /**
   * Returns the freight lane active price
   * @returns {Number}
   */
  const freightLaneFreightCost = useMemo(() => {
    if (!freightLaneActivePriceData) return null
    const materialCost = convertUM(
      freightLaneActivePriceData.costPerUm * netWeight,
      defaultYardUm,
      freightLaneActivePriceData.um,
    )
    const preFuelTotal = roundTo(freightLaneActivePriceData.minCost + materialCost, ROUNDING_PRECISIONS.COST)
    const fuelCost = roundTo((preFuelTotal * freightLaneActivePriceData.fuelSurcharge) / 100, ROUNDING_PRECISIONS.COST)

    const total = roundTo(preFuelTotal + fuelCost, ROUNDING_PRECISIONS.COST)

    return total
  }, [freightLaneActivePriceData, netWeight])

  /**
   * Returns an object with the freight cost info by source
   * @returns {Object}
   */
  const allFreightCostInfoBySource = useMemo(() => {
    return {
      contract: {
        freightCost: contractFreightCost ?? null,
        freightChargeback: contractFreightChargeback ?? null,
        enabled: contract?.freightPricing?.consumerFreightEnabled ?? false,
        sourceLabel: 'Contract',
        sourceKey: 'contract',
      },
      location: {
        freightCost: locationFreightCost ?? null,
        freightChargeback: locationFreightChargeback ?? null,
        enabled:
          (ticketType === 'OBT'
            ? location?.freightPricing?.consumerFreightEnabled
            : location?.freightPricing?.supplierFreightEnabled) ?? false,
        sourceLabel: 'Location',
        sourceKey: 'location',
      },
      freightLane: {
        freightCost: freightLaneFreightCost ?? null,
        freightChargeback: freightLaneFreightCost ?? null, // Freight lanes don't have chargebacks
        enabled: freightLaneFreightCost !== null,
        sourceLabel: 'Freight Lane',
        sourceKey: 'freightLane',
      },
    }
  }, [
    locationFreightCost,
    locationFreightChargeback,
    contractFreightCost,
    contractFreightChargeback,
    freightLaneFreightCost,
    location,
    contract,
    ticketType,
  ])

  /**
   * Returns the priority freight cost info based on the freight cost info by source
   * @returns {Object}
   */
  const priorityFreightCostInfo = useMemo(() => {
    if (allFreightCostInfoBySource.contract?.enabled) {
      return allFreightCostInfoBySource.contract
    }
    if (allFreightCostInfoBySource.location?.enabled) {
      return allFreightCostInfoBySource.location
    }
    if (allFreightCostInfoBySource.freightLane?.enabled) {
      return allFreightCostInfoBySource.freightLane
    }
    return null
  }, [allFreightCostInfoBySource])

  return {
    isLoading: isLoadingFreightLanesData,
    allFreightCostInfoBySource,
    priorityFreightCostInfo,
  }
}
